import type { ClientId, InstrumentId, SafetyDeviceId, InstrumentCalibrationId, SafetyDeviceTestId } from "../../../shared/kernel/identifiers.js";
import type {
  Instrument,
  InstrumentCalibration,
  InstrumentRepository,
  SafetyDevice,
  SafetyDeviceTest,
  SafetyDeviceRepository,
  TechnicalAssetDocument
} from "../../../equipment/equipment-domain.js";

export class InMemoryInstrumentRepository implements InstrumentRepository {
  private instruments = new Map<string, Instrument>();
  private calibrations = new Map<string, InstrumentCalibration>();
  private documents = new Map<string, TechnicalAssetDocument>();

  constructor() {
    try {
      const instData = localStorage.getItem('inspect_instruments');
      if (instData) this.instruments = new Map(JSON.parse(instData));
      const calData = localStorage.getItem('inspect_instrument_calibrations');
      if (calData) this.calibrations = new Map(JSON.parse(calData));
      const docData = localStorage.getItem('inspect_instrument_documents');
      if (docData) this.documents = new Map(JSON.parse(docData));
    } catch (e) {
      console.warn("Failed to load instruments from localStorage", e);
    }
  }

  private persist() {
    try {
      localStorage.setItem('inspect_instruments', JSON.stringify(Array.from(this.instruments.entries())));
      localStorage.setItem('inspect_instrument_calibrations', JSON.stringify(Array.from(this.calibrations.entries())));
      localStorage.setItem('inspect_instrument_documents', JSON.stringify(Array.from(this.documents.entries())));
    } catch (e) {
      console.warn("Failed to save instruments to localStorage", e);
    }
  }

  async getInstrument(clientId: ClientId, id: InstrumentId): Promise<Instrument | null> {
    const inst = this.instruments.get(id);
    if (inst && inst.clientId === clientId) {
      return inst;
    }
    return null;
  }

  async listInstruments(clientId: ClientId, filters?: { equipmentId?: string; areaId?: string; status?: string }): Promise<Instrument[]> {
    return Array.from(this.instruments.values()).filter(inst => {
      if (inst.clientId !== clientId) return false;
      if (filters?.equipmentId && inst.equipmentId !== filters.equipmentId) return false;
      if (filters?.areaId && inst.areaId !== filters.areaId) return false;
      if (filters?.status && filters.status !== 'ALL' && inst.status !== filters.status) return false;
      return true;
    });
  }

  async saveInstrument(instrument: Instrument): Promise<void> {
    this.instruments.set(instrument.id, instrument);
    this.persist();
  }

  async deleteInstrument(clientId: ClientId, id: InstrumentId): Promise<void> {
    const inst = this.instruments.get(id);
    if (inst && inst.clientId === clientId) {
      this.instruments.delete(id);
      this.persist();
    }
  }

  async saveCalibration(calibration: InstrumentCalibration): Promise<void> {
    this.calibrations.set(calibration.id, calibration);
    this.persist();
  }

  async listCalibrations(clientId: ClientId, instrumentId: InstrumentId): Promise<InstrumentCalibration[]> {
    return Array.from(this.calibrations.values())
      .filter(c => c.clientId === clientId && c.instrumentId === instrumentId)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }

  async deleteCalibration(clientId: ClientId, calibrationId: InstrumentCalibrationId): Promise<void> {
    const cal = this.calibrations.get(calibrationId);
    if (cal && cal.clientId === clientId) {
      this.calibrations.delete(calibrationId);
      this.persist();
    }
  }

  async saveDocument(doc: TechnicalAssetDocument): Promise<void> {
    this.documents.set(doc.id, doc);
    this.persist();
  }

  async listDocuments(clientId: ClientId, instrumentId: string): Promise<TechnicalAssetDocument[]> {
    return Array.from(this.documents.values()).filter(
      d => d.clientId === clientId && d.assetId === instrumentId && d.assetType === 'INSTRUMENT'
    );
  }

  async removeDocument(clientId: ClientId, documentId: string): Promise<void> {
    const doc = this.documents.get(documentId);
    if (doc && doc.clientId === clientId) {
      this.documents.delete(documentId);
      this.persist();
    }
  }
}

export class InMemorySafetyDeviceRepository implements SafetyDeviceRepository {
  private devices = new Map<string, SafetyDevice>();
  private tests = new Map<string, SafetyDeviceTest>();
  private documents = new Map<string, TechnicalAssetDocument>();

  constructor() {
    try {
      const devData = localStorage.getItem('inspect_safety_devices');
      if (devData) this.devices = new Map(JSON.parse(devData));
      const testData = localStorage.getItem('inspect_safety_device_tests');
      if (testData) this.tests = new Map(JSON.parse(testData));
      const docData = localStorage.getItem('inspect_safety_device_documents');
      if (docData) this.documents = new Map(JSON.parse(docData));
    } catch (e) {
      console.warn("Failed to load safety devices from localStorage", e);
    }
  }

  private persist() {
    try {
      localStorage.setItem('inspect_safety_devices', JSON.stringify(Array.from(this.devices.entries())));
      localStorage.setItem('inspect_safety_device_tests', JSON.stringify(Array.from(this.tests.entries())));
      localStorage.setItem('inspect_safety_device_documents', JSON.stringify(Array.from(this.documents.entries())));
    } catch (e) {
      console.warn("Failed to save safety devices to localStorage", e);
    }
  }

  async getSafetyDevice(clientId: ClientId, id: SafetyDeviceId): Promise<SafetyDevice | null> {
    const dev = this.devices.get(id);
    if (dev && dev.clientId === clientId) {
      return dev;
    }
    return null;
  }

  async listSafetyDevices(clientId: ClientId, filters?: { protectedEquipmentId?: string; areaId?: string; status?: string }): Promise<SafetyDevice[]> {
    return Array.from(this.devices.values()).filter(dev => {
      if (dev.clientId !== clientId) return false;
      if (filters?.protectedEquipmentId && dev.protectedEquipmentId !== filters.protectedEquipmentId) return false;
      if (filters?.areaId && dev.areaId !== filters.areaId) return false;
      if (filters?.status && filters.status !== 'ALL' && dev.status !== filters.status) return false;
      return true;
    });
  }

  async saveSafetyDevice(device: SafetyDevice): Promise<void> {
    this.devices.set(device.id, device);
    this.persist();
  }

  async deleteSafetyDevice(clientId: ClientId, id: SafetyDeviceId): Promise<void> {
    const dev = this.devices.get(id);
    if (dev && dev.clientId === clientId) {
      this.devices.delete(id);
      this.persist();
    }
  }

  async saveTest(test: SafetyDeviceTest): Promise<void> {
    this.tests.set(test.id, test);
    this.persist();
  }

  async listTests(clientId: ClientId, safetyDeviceId: SafetyDeviceId): Promise<SafetyDeviceTest[]> {
    return Array.from(this.tests.values())
      .filter(t => t.clientId === clientId && t.safetyDeviceId === safetyDeviceId)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }

  async deleteTest(clientId: ClientId, testId: SafetyDeviceTestId): Promise<void> {
    const t = this.tests.get(testId);
    if (t && t.clientId === clientId) {
      this.tests.delete(testId);
      this.persist();
    }
  }

  async saveDocument(doc: TechnicalAssetDocument): Promise<void> {
    this.documents.set(doc.id, doc);
    this.persist();
  }

  async listDocuments(clientId: ClientId, safetyDeviceId: string): Promise<TechnicalAssetDocument[]> {
    return Array.from(this.documents.values()).filter(
      d => d.clientId === clientId && d.assetId === safetyDeviceId && d.assetType === 'SAFETY_DEVICE'
    );
  }

  async removeDocument(clientId: ClientId, documentId: string): Promise<void> {
    const doc = this.documents.get(documentId);
    if (doc && doc.clientId === clientId) {
      this.documents.delete(documentId);
      this.persist();
    }
  }
}
